import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Star, Shield, Clock, Award, TrendingUp, Users, CheckCircle, Sparkles, Zap, ArrowRight, MessageCircle, ChevronLeft, ChevronRight, Quote, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { PopupForm } from '../components/PopupForm';
import { pricingPlans, testimonials, faqs, countryCodes, processSteps } from '../mock';
import { toast } from 'sonner';
import { getGeoLocation, submitLead } from '../services/api';

export const LandingPage = () => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    contactNumber: '',
    message: ''
  });

  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const itemsToShow = 3; // Show 3 testimonials at a time
  const totalSlides = testimonials.length;

  useEffect(() => {
    fetchGeoLocation();
  }, []);

  const fetchGeoLocation = async () => {
    const result = await getGeoLocation();
    if (result.success) {
      setGeoData(result.data);
      const countryCode = countryCodes.find(c => c.country === result.data.countryCode);
      if (countryCode) {
        setFormData(prev => ({ 
          ...prev, 
          countryCode: countryCode.code,
          country: result.data.countryCode 
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Extract dial code number from string (e.g., "+1" -> 1)
      const dialCode = parseInt(formData.countryCode.replace('+', ''));
      
      // Clean phone number - remove all non-numeric characters
      const cleanedPhone = formData.contactNumber.replace(/\D/g, '');
      
      // Validate phone number length (should be at least 7 digits)
      if (cleanedPhone.length < 7) {
        toast.error('Please enter a valid phone number (at least 7 digits)');
        setIsLoading(false);
        return;
      }

      const leadData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        countryDialCode: dialCode,
        country: formData.country,
        phoneNumber: cleanedPhone, // Already cleaned to numeric only
        description: formData.message.trim() || 'Landing page form submission'
      };

      const result = await submitLead(leadData, geoData);

      if (result.success) {
        toast.success('Thank you! Your application has been submitted successfully.');
        
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          country: 'US',
          contactNumber: '',
          message: ''
        });
        
        // Redirect to thank you page with customer ID
        setTimeout(() => {
          navigate(result.redirectUrl);
        }, 1000);
      } else {
        toast.error(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const timer = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTestimonial((prev) => (prev + 1) % totalSlides);
        setIsAnimating(false);
      }, 100);
    }, 6000);
    return () => clearInterval(timer);
  }, [totalSlides]);

  const goToPreviousTestimonial = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTestimonial((prev) => (prev - 1 + totalSlides) % totalSlides);
        setIsAnimating(false);
      }, 100);
    }
  };

  const goToNextTestimonial = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTestimonial((prev) => (prev + 1) % totalSlides);
        setIsAnimating(false);
      }, 100);
    }
  };

  const goToTestimonialSlide = (index) => {
    if (!isAnimating && index !== currentTestimonial) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTestimonial(index);
        setIsAnimating(false);
      }, 100);
    }
  };

  // Get 3 testimonials starting from currentIndex, wrapping around if needed
  const getCurrentTestimonials = () => {
    const items = [];
    for (let i = 0; i < itemsToShow; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      items.push(testimonials[index]);
    }
    return items;
  };

  return (
    <div className="pt-20 overflow-x-hidden">
      <Helmet>
        <title>Expert Trademark Registration | Fast & Affordable Brand Protection Services</title>
        <meta name="description" content="Get your trademark registered quickly and affordably. Comprehensive brand protection with expert guidance, trademark searches, and USPTO filing. Trusted by 10,000+ businesses nationwide." />
        <meta name="keywords" content="trademark registration, affordable trademark, fast trademark filing, brand protection, USPTO registration, trademark attorney, business trademark" />
        <meta property="og:title" content="Expert Trademark Registration | Fast & Affordable Brand Protection" />
        <meta property="og:description" content="Get your trademark registered quickly and affordably. Trusted by 10,000+ businesses nationwide." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Expert Trademark Registration | Fast & Affordable" />
        <meta name="twitter:description" content="Get your trademark registered quickly and affordably. Trusted by 10,000+ businesses nationwide." />
      </Helmet>

      {/* Enhanced Hero Section with Form */}
      <section className="relative bg-gradient-to-br from-purple-600 via-blue-600 to-blue-700 text-white py-10 md:py-12 overflow-hidden min-h-[calc(100vh-80px)] max-h-[1000px] flex items-center">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        {/* Decorative Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzYyNTQ4OTg5fDA&ixlib=rb-4.1.0&q=85" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Floating Decorative Icons */}
        <div className="absolute inset-0 pointer-events-none hidden lg:block">
          <div className="absolute top-20 left-1/4 animate-float" style={{ animationDelay: '0s' }}>
            <Zap className="w-12 h-12 text-white/20" />
          </div>
          <div className="absolute top-32 right-1/4 animate-float" style={{ animationDelay: '2s' }}>
            <Sparkles className="w-14 h-14 text-white/20" />
          </div>
          <div className="absolute bottom-24 left-1/3 animate-float" style={{ animationDelay: '1s' }}>
            <Star className="w-12 h-12 text-white/20" />
          </div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-7xl mx-auto">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-5 py-2 mb-5 animate-slide-in">
                <Star className="w-5 h-5 fill-current" />
                <span className="text-sm md:text-base font-bold">America's #1 Trademark Registration Service</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 leading-tight animate-slide-in">
                Secure Your Brand.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-white to-purple-200">Own Your Future.</span>
              </h1>
              <p className="text-base md:text-lg mb-6 text-white/95 leading-relaxed animate-slide-in max-w-2xl">
                Fast, affordable trademark registration with expert legal support. Protect your business name, logo, and brand identity with federal trademark protection in as little as 24 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start animate-slide-in">
                <Button
                  className="bg-white text-purple-600 hover:bg-blue-50 border-0 h-12 px-8 text-base font-bold shadow-xl"
                  onClick={() => setIsPopupOpen(true)}
                >
                  <Zap className="mr-2" size={20} />
                  Get Started Now
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm h-12 px-7 text-base font-semibold js-chat"
                  onClick={(e) => { e.preventDefault(); if (window.OpenTawktrik) window.OpenTawktrik(); }}
                >
                  <MessageCircle className="mr-2" size={18} />
                  Chat Now
                </Button>
              </div>
            </div>

            {/* Right Form - Compact */}
            <div className="bg-white rounded-2xl p-5 shadow-2xl backdrop-blur-lg animate-slide-in border-2 border-blue-100">
              <div className="text-center mb-4">
                <div className="bg-gradient-to-br from-purple-500 to-blue-600 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <Shield className="text-white" size={24} />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">Register Your Trademark Today</h2>
                <p className="text-xs text-gray-600">Quick consultation - Respond within 2 hours</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Name *</label>
                  <Input
                    required
                    disabled={isLoading}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    className="h-9 text-sm text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    required
                    disabled={isLoading}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    className="h-9 text-sm text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Number *</label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.countryCode}
                      onValueChange={(value) => {
                        const selected = countryCodes.find(c => c.code === value);
                        setFormData({ 
                          ...formData, 
                          countryCode: value,
                          country: selected?.country || 'US'
                        });
                      }}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="w-24 h-9 text-sm text-gray-900">
                        <SelectValue placeholder="+1" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {countryCodes.map((item) => (
                          <SelectItem key={item.country} value={item.code}>
                            {item.code} {item.country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      disabled={isLoading}
                      value={formData.contactNumber}
                      onChange={(e) => {
                        // Remove all non-numeric characters and limit to 15 digits
                        const value = e.target.value.replace(/\D/g, '').slice(0, 15);
                        setFormData({ ...formData, contactNumber: value });
                      }}
                      onKeyPress={(e) => {
                        // Prevent non-numeric key presses
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="5551234567"
                      maxLength={15}
                      pattern="[0-9]*"
                      inputMode="numeric"
                      className="flex-1 h-9 text-sm text-gray-900"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Message</label>
                  <Textarea
                    disabled={isLoading}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="I need help registering my trademark for..."
                    rows={2}
                    className="resize-none text-sm text-gray-900"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 h-10 text-sm font-semibold shadow-lg hover:shadow-xl transition-all"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Get Started Today →'
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80L60 73.3C120 66.7 240 53.3 360 46.7C480 40 600 40 720 43.3C840 46.7 960 53.3 1080 56.7C1200 60 1320 60 1380 60L1440 60V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0V80Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Why Trademark Registration - Enhanced */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-100 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ✦ BENEFITS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Trademark Registration is Essential
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A registered trademark protects your brand identity and provides legal rights crucial for business growth
            </p>
          </div>
          
          {/* Mobile: Carousel */}
          <div className="md:hidden px-4">
            <Carousel className="w-full max-w-sm mx-auto" opts={{ align: "center" }}>
              <CarouselContent>
                <CarouselItem>
                  <div className="p-1">
                    <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
                      <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Shield className="text-white" size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Legal Protection</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                        Exclusive nationwide rights to use your trademark and legal grounds to prevent others from using similar marks
                      </p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-1">
                    <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
                      <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <TrendingUp className="text-white" size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Brand Value</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                        Increases your brand's market value and makes your business more attractive to investors and partners
                      </p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-1">
                    <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
                      <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Award className="text-white" size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Competitive Edge</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                        Distinguishes your products and services in the marketplace and builds customer trust and recognition
                      </p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-1">
                    <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
                      <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Clock className="text-white" size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Long-term Asset</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                        A registered trademark can be renewed indefinitely, providing perpetual protection for your brand
                      </p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-1">
                    <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
                      <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Users className="text-white" size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Customer Trust</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                        The ® symbol signals legitimacy and quality, helping build customer confidence in your brand
                      </p>
                    </div>
                  </div>
                </CarouselItem>
                <CarouselItem>
                  <div className="p-1">
                    <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
                      <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Zap className="text-white" size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Online Protection</h3>
                      <p className="text-base text-gray-600 leading-relaxed">
                        Essential for domain name disputes, social media verification, and e-commerce marketplace protection
                      </p>
                    </div>
                  </div>
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
              <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Shield className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Legal Protection</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Exclusive nationwide rights to use your trademark and legal grounds to prevent others from using similar marks
              </p>
            </div>

            <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
              <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <TrendingUp className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Brand Value</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Increases your brand's market value and makes your business more attractive to investors and partners
              </p>
            </div>

            <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
              <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Competitive Edge</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Distinguishes your products and services in the marketplace and builds customer trust and recognition
              </p>
            </div>

            <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
              <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Clock className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Long-term Asset</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                A registered trademark can be renewed indefinitely, providing perpetual protection for your brand
              </p>
            </div>

            <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
              <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Customer Trust</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                The ® symbol signals legitimacy and quality, helping build customer confidence in your brand
              </p>
            </div>

            <div className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
              <div className="bg-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <Zap className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Online Protection</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Essential for domain name disputes, social media verification, and e-commerce marketplace protection
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps - Enhanced */}
      <section className="py-20 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Simple 3-Step Process</h2>
            <p className="text-lg text-gray-600">
              We handle everything from search to registration
            </p>
          </div>
          
          {/* Mobile: Carousel */}
          <div className="md:hidden px-4">
            <Carousel className="w-full max-w-sm mx-auto" opts={{ align: "center" }}>
              <CarouselContent>
                {processSteps.map((step, index) => (
                  <CarouselItem key={step.id}>
                    <div className="p-1">
                      <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all">
                        <div className="bg-primary w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                          <span className="text-3xl font-bold text-white">{index + 1}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{step.title}</h3>
                        <p className="text-base text-gray-600 leading-relaxed text-center">{step.description}</p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
            {processSteps.map((step, index) => (
              <div key={step.id} className="relative text-center flex">
                <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all flex flex-col w-full h-full">
                  <div className="bg-primary w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-3xl font-bold text-white">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed flex-1">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="text-gray-300" size={32} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button
              onClick={() => setIsPopupOpen(true)}
              className="bg-primary hover:bg-primary/90 h-11 px-8 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Start My Registration Now →
            </Button>
            <Button
              onClick={(e) => { e.preventDefault(); if (window.OpenTawktrik) window.OpenTawktrik(); }}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-blue-50 h-11 px-8 text-base font-semibold flex items-center gap-2 js-chat"
            >
              <MessageCircle size={18} />
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section - Fixed Layout (No Carousel) */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Affordable Trademark Registration Plans
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the protection level that fits your needs
            </p>
          </div>

          {/* Fixed vertical stack on mobile, grid on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl shadow-xl overflow-hidden transition-transform hover:scale-105 flex flex-col border-2 ${
                  plan.popular ? 'border-blue-600' : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="bg-primary text-white text-center py-2 text-sm font-bold">
                    MOST POPULAR
                  </div>
                )}
                
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-primary">${plan.price}</span>
                    <span className="text-gray-600 text-base"> + Federal Filing Fee</span>
                  </div>

                  <div className="flex-1 overflow-y-auto max-h-64 mb-4">
                    <ul className="space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Check className="text-primary flex-shrink-0 mt-0.5" size={20} />
                          <span className="text-base text-gray-700 leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-sm text-gray-500 italic mb-4">{plan.note}</p>

                  <div className="mt-auto space-y-2">
                    <Button
                      onClick={() => setIsPopupOpen(true)}
                      className={`w-full h-11 text-base font-semibold ${
                        plan.popular
                          ? 'bg-primary hover:bg-primary/90'
                          : 'bg-gray-900 hover:bg-gray-800'
                      }`}
                    >
                      Select {plan.name}
                    </Button>
                    <Button
                      onClick={(e) => { e.preventDefault(); if (window.OpenTawktrik) window.OpenTawktrik(); }}
                      variant="outline"
                      className="w-full h-11 text-base border-2 border-gray-300 hover:bg-gray-50 flex items-center justify-center gap-2 js-chat"
                    >
                      <MessageCircle size={18} />
                      Chat Now
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Enhanced with 3 Cards Sliding */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-100 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
              TESTIMONIALS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-gray-600">Join thousands of satisfied trademark owners</p>
          </div>

          <div className="max-w-7xl mx-auto relative">
            {/* Desktop - 3 cards with slide animation */}
            <div className="hidden md:block overflow-hidden">
              <div 
                className={`grid grid-cols-3 gap-6 transition-all duration-700 ease-in-out ${
                  isAnimating ? 'opacity-0 transform translate-x-8' : 'opacity-100 transform translate-x-0'
                }`}
              >
                {getCurrentTestimonials().map((testimonial, idx) => (
                  <div 
                    key={`${testimonial.id}-${currentTestimonial}-${idx}`} 
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <Quote className="text-primary/20" size={40} />
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-base text-gray-700 mb-6 leading-relaxed line-clamp-4">
                      "{testimonial.text}"
                    </p>
                    
                    <div className="border-t pt-4">
                      <p className="font-bold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-primary font-medium">{testimonial.designation}</p>
                      <p className="text-sm text-gray-600">{testimonial.company}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile - 1 card with slide animation */}
            <div className="md:hidden overflow-hidden">
              <div 
                className={`transition-all duration-700 ease-in-out ${
                  isAnimating ? 'opacity-0 transform translate-x-8' : 'opacity-100 transform translate-x-0'
                }`}
              >
                {getCurrentTestimonials().slice(0, 1).map((testimonial) => (
                  <div key={`${testimonial.id}-${currentTestimonial}-mobile`} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                      <Quote className="text-primary/20" size={40} />
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-base text-gray-700 mb-6 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    
                    <div className="border-t pt-4">
                      <p className="font-bold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-primary font-medium">{testimonial.designation}</p>
                      <p className="text-sm text-gray-600">{testimonial.company}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={goToPreviousTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 bg-white hover:bg-primary hover:text-white p-3 rounded-full shadow-lg transition-all border-2 border-gray-200 hover:border-primary"
            >
              <ChevronLeft size={24} />
            </button>
            
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 bg-white hover:bg-primary hover:text-white p-3 rounded-full shadow-lg transition-all border-2 border-gray-200 hover:border-primary"
            >
              <ChevronRight size={24} />
            </button>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {[...Array(totalSlides)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonialSlide(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentTestimonial ? 'bg-primary w-8' : 'bg-gray-300 w-2'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs - Enhanced */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about trademark registration</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-gradient-to-r from-blue-50 to-white rounded-2xl px-6 border-none shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left font-bold text-gray-900 hover:text-blue-600 py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-gray-600 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA - Enhanced */}
      <section className="relative py-24 bg-gradient-to-r from-blue-600 to-blue-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-white/95 leading-relaxed">
            Don't wait until it's too late. Secure your trademark today and protect your business for tomorrow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setIsPopupOpen(true)}
              className="bg-white text-blue-600 hover:bg-gray-100 h-14 px-10 text-lg font-bold shadow-xl hover:shadow-2xl"
            >
              Register My Trademark →
            </Button>
            <Button
              onClick={(e) => { e.preventDefault(); if (window.OpenTawktrik) window.OpenTawktrik(); }}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 h-14 px-10 text-lg font-bold flex items-center gap-2 js-chat"
            >
              <MessageCircle size={20} />
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* Simple Footer for Landing Page */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-400">
              © 2026 Trademark Frontline. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Popup Form */}
      <PopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};
